/**
 * EL BUZÓN. Guarda el aviso que llega antes de que la aplicación exista.
 *
 * EL FALLO (02-09-2026, tercera ronda con el usuario). Con la app YA ABIERTA,
 * tocar un aviso abre la ficha de la tarea: funciona. Con la app CERRADA, abre
 * el Planificador y la ficha no sale. Ese contraste es el que lo explica todo,
 * porque descarta las dos cosas que llevábamos dos rondas mirando: si el aviso
 * en caliente abre la ficha, entonces el `data` se lee bien Y la ruta se calcula
 * bien. Lo que falla no es QUÉ llega, es CUÁNDO.
 *
 * `window.onNotificationEvent` es una llamada de UN SOLO DISPARO, y hasta ahora
 * se instalaba dentro de un `useEffect` de React — o sea, al final de esta cola:
 *
 *   HTML → descargar el paquete (1,5 MB) → interpretarlo → i18n → React monta
 *   → corre el efecto → AHORA ya hay quien escuche.
 *
 * El envoltorio nativo guarda el aviso del arranque en frío y lo entrega cuando
 * la página termina de cargar. Con la app abierta eso ya ha pasado hace rato y
 * hay quien lo recoja. Con la app cerrada y datos móviles, la descarga del
 * paquete sola puede tardar más que la entrega: el envoltorio llama a una
 * propiedad que todavía NO EXISTE, no hay error en ninguna parte, y el aviso se
 * pierde para siempre. El usuario aterriza en la pantalla que el WebView tenía
 * guardada de la sesión anterior, que estos días es justo el Planificador — por
 * eso parecía que el enlace medio funcionaba.
 *
 * La solución no es correr más, es tener el buzón puesto ANTES. Se instala en
 * dos sitios, a propósito:
 *
 *   1. En un `<script>` suelto dentro del `<head>` de `index.html`, que corre
 *      mientras se lee el HTML — antes incluso de empezar a bajar el paquete.
 *      Ése es el pronto de verdad.
 *   2. Aquí, importado desde `main.tsx` antes de pintar nada, por si algún día
 *      Lovable regenera el `index.html` y se lleva el script por delante. Ver
 *      `avisoPendiente.guard.test.ts`, que falla si eso pasa.
 *
 * Y NO SE BORRA AL DESMONTAR. El componente pone su propia función mientras vive
 * y vuelve a dejar el buzón al irse; antes hacía `delete`, que deja otra vez la
 * propiedad sin existir.
 */

declare global {
  interface Window {
    /** Lo llama el envoltorio nativo al tocar un aviso. */
    onNotificationEvent?: (payload: unknown) => void;
    /** El hueco que usa el script de `index.html`, que no puede importar nada. */
    __ultureAvisoPendiente?: unknown;
  }
}

/** Lo que llegó sin nadie escuchando. */
let guardado: unknown = null;

/* HUBO UN AVISO, aunque ya se haya recogido. Es distinto de `guardado`, que se
   vacía al recogerlo: esto se queda puesto todo el arranque, porque quien lo
   pregunta —la pantalla de bienvenida— puede montar antes o después que el
   componente que recoge. */
let huboAviso = false;

/** Quien quiera enterarse en el momento (la pantalla de bienvenida). */
const oyentes = new Set<() => void>();

function anotarAviso() {
  huboAviso = true;
  for (const oyente of [...oyentes]) {
    try { oyente(); } catch { /* un oyente roto no puede tumbar el aviso */ }
  }
}

/** ¿Se ha abierto la app tocando un aviso? */
export function huboAvisoDeArranque(): boolean {
  return huboAviso;
}

/** Avisa cuando llegue uno. Devuelve la función para dejar de escuchar. */
export function alLlegarUnAviso(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

/** Trae lo que hubiera dejado el script del `index.html`. */
function vaciarElHuecoDelHtml(): void {
  if (typeof window === "undefined") return;
  const delHtml = window.__ultureAvisoPendiente;
  if (delHtml !== undefined && delHtml !== null) {
    guardado = delHtml;
    window.__ultureAvisoPendiente = null;
    anotarAviso();
  }
}

/**
 * Pone el buzón. Llamarlo tantas veces como haga falta: es idempotente y no pisa
 * un aviso ya guardado.
 */
export function instalarBuzonDeAvisos(): void {
  if (typeof window === "undefined") return;
  vaciarElHuecoDelHtml();
  window.onNotificationEvent = (payload: unknown) => {
    guardado = payload;
    anotarAviso();
  };
}

/**
 * Recoge lo que hubiera, y deja el buzón vacío.
 *
 * Devuelve `null` cuando no había nada, que es el caso normal: solo trae algo en
 * el arranque en frío desde un aviso.
 */
export function recogerAvisoPendiente(): unknown {
  vaciarElHuecoDelHtml();
  const aviso = guardado;
  guardado = null;
  return aviso ?? null;
}

/** Para los tests: deja esto como recién cargado. */
export function vaciarBuzonDeAvisos(): void {
  guardado = null;
  huboAviso = false;
  oyentes.clear();
  if (typeof window !== "undefined") window.__ultureAvisoPendiente = null;
}
